import { z } from "zod";

import languageCodeList from "../../constants/languages";

import type { LanguageCode } from "../../api/ProjectAPI.types";
import type {
  CreateDisplayTranslationResponse,
  DisplayTranslation,
} from "../../api/SmartlistAPI.types";
import type { Picture, TranslationsIndexes } from "../../types";

export const displayTranslationSchema = z.object({
  language: z.enum(languageCodeList),
  language_uuid: z.string().optional(),
  default: z.boolean(),
  uuid: z.string().optional(),
  title: z.string().optional(),
  url: z.string().optional(),
  picture: z.custom<Picture>().optional(),
});

export const displayTranslationsFormSchema = z.object({
  translations: z.array(displayTranslationSchema),
});

export interface DisplayTranslationFormField {
  language: LanguageCode;
  language_uuid?: string;
  default: boolean;
  uuid?: string;
  title?: string;
  url?: string;
  picture?: Picture;
}

/**
 * Converts API DisplayTranslation objects to the form field format.
 */
export function apiToFormTranslations(
  translations: DisplayTranslation[],
): DisplayTranslationFormField[] {
  return [...translations]
    .sort((a, b) => {
      if (a.default) return -1;
      if (b.default) return 1;
      return a.language.localeCompare(b.language);
    })
    .map((translation) => ({
      language: translation.language as LanguageCode,
      language_uuid: translation.language_uuid,
      default: translation.default,
      uuid: translation.uuid,
      title: translation.title ?? "",
      url: translation.url ?? "",
      picture: translation.picture ?? undefined,
    }));
}

/**
 * Adapts a CreateDisplayTranslationResponse into the DisplayTranslationFormField shape.
 */
export function createTranslationResponseToFormField(
  response: CreateDisplayTranslationResponse,
  language: LanguageCode,
): DisplayTranslationFormField {
  return {
    uuid: response.uuid,
    default: response.default,
    language_uuid: response.language_uuid,
    title: response.title ?? "",
    url: response.url ?? "",
    language,
  };
}

export interface DisplayTranslationToCreate {
  uuid?: string;
  default: boolean;
  language: string;
  language_uuid: string;
  title: string;
  url: string;
  picture?: Picture | null;
}

/**
 * Converts form field values back to the API shape.
 */
export function formToApiTranslations(
  formTranslations: DisplayTranslationFormField[],
): {
  translations: DisplayTranslationToCreate[];
  translations_indexes: TranslationsIndexes;
} {
  const translations: DisplayTranslationToCreate[] = formTranslations.map(
    (field) => ({
      uuid: field.uuid,
      default: field.default,
      language: field.language,
      language_uuid: field.language_uuid ?? "",
      title: field.title ?? "",
      url: field.url ?? "",
      picture: field.picture,
    }),
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const language_uuid: Record<string, number[]> = {};
  let defaultIndex = 0;

  formTranslations.forEach((field, index) => {
    if (field.language_uuid) {
      language_uuid[field.language_uuid] = [index];
    }
    if (field.default) {
      defaultIndex = index;
    }
  });

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const translations_indexes: TranslationsIndexes = {
    language_uuid,
    default: { true: [defaultIndex] },
  };

  return { translations, translations_indexes };
}
